import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Cake, MessageCircle, Send, RefreshCw, PartyPopper, Users, Baby, UserCheck } from 'lucide-react';
import { toast } from 'sonner';
import { Card } from '../components/ui/card';
import Navigation from '../components/Navigation';
import { getAdminHeaders } from '../lib/auth';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Aniversariantes = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sentMessages, setSentMessages] = useState({});

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/reports/birthday-messages`, { headers: getAdminHeaders() });
      setData(res.data);
    } catch { toast.error('Erro ao carregar aniversariantes'); }
    setLoading(false);
  };

  const sendWhatsApp = (msg, idx) => {
    if (!msg.whatsapp_link) {
      toast.error(`Link do grupo "${msg.grupo_destino}" nao configurado. Configure em Admin > Links WhatsApp.`);
      return;
    }
    const encoded = encodeURIComponent(msg.mensagem);
    // Extract the group invite hash and open WhatsApp Web
    window.open(`https://wa.me/?text=${encoded}`, '_blank');
    setSentMessages(prev => ({ ...prev, [idx]: true }));
    toast.success(`Mensagem copiada! Cole no grupo "${msg.grupo_destino}"`);
  };

  const copyMessage = (msg) => {
    navigator.clipboard.writeText(msg.mensagem).then(() => {
      toast.success('Mensagem copiada para a area de transferencia!');
    }).catch(() => toast.error('Erro ao copiar'));
  };

  if (loading) return (
    <div className="min-h-screen bg-[#F9F9F7] flex items-center justify-center">
      <RefreshCw className="w-8 h-8 animate-spin text-[#1A4D2E]" />
    </div>
  );

  const messages = data?.messages || [];
  const criancas = messages.filter(m => m.tipo === 'crianca');
  const voluntarios = messages.filter(m => m.tipo === 'voluntario');

  return (
    <div className="min-h-screen bg-[#F9F9F7] pb-24">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#F4D35E] to-[#E8C74A] text-[#1A4D2E] px-4 sm:px-6 py-6">
        <div className="max-w-3xl mx-auto">
          <button onClick={() => navigate(-1)} className="hover:bg-black/5 rounded-full p-2 transition-all mb-2" data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-3">
            <Cake className="w-7 h-7" />
            <div>
              <h1 className="font-nunito font-bold text-2xl sm:text-3xl">Aniversariantes</h1>
              <p className="font-inter text-[#1A4D2E]/70 text-sm mt-1">
                {data?.date ? new Date(data.date + 'T12:00:00').toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' }) : ''}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-6 space-y-4">
        {messages.length === 0 ? (
          <Card className="bg-white rounded-2xl p-12 text-center shadow-md border border-[#E1ECE5]" data-testid="no-birthdays">
            <PartyPopper className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="font-nunito font-bold text-lg text-gray-500">Nenhum aniversariante hoje</p>
            <p className="font-inter text-sm text-gray-400 mt-1">Volte amanha para verificar!</p>
          </Card>
        ) : (
          <>
            {/* Summary */}
            <Card className="bg-white rounded-2xl p-4 shadow-sm border border-[#E1ECE5] flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="bg-[#F4D35E]/20 rounded-full p-2.5">
                  <PartyPopper className="w-6 h-6 text-[#E8C74A]" />
                </div>
                <div>
                  <p className="font-nunito font-bold text-lg text-[#1A4D2E]">{messages.length} aniversariante{messages.length !== 1 ? 's' : ''} hoje!</p>
                  <p className="font-inter text-xs text-gray-500">{criancas.length} crianca{criancas.length !== 1 ? 's' : ''}, {voluntarios.length} voluntario{voluntarios.length !== 1 ? 's' : ''}</p>
                </div>
              </div>
            </Card>

            {/* Children */}
            {criancas.length > 0 && (
              <div>
                <h3 className="font-nunito font-bold text-sm text-[#1A4D2E] mb-2 flex items-center gap-2">
                  <Baby className="w-4 h-4" /> Criancas
                </h3>
                <div className="space-y-3">
                  {criancas.map((msg, idx) => (
                    <Card key={idx} className="bg-white rounded-xl p-4 shadow-sm border border-[#E1ECE5]" data-testid={`birthday-child-${idx}`}>
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <p className="font-nunito font-bold text-base text-[#1A4D2E]">{msg.nome}</p>
                          <p className="font-inter text-xs text-gray-500">{msg.idade} anos - Grupo: <span className="font-semibold text-[#1A4D2E]">{msg.grupo_destino}</span></p>
                        </div>
                        <Cake className="w-6 h-6 text-[#F4D35E]" />
                      </div>

                      <div className="bg-gray-50 rounded-lg p-3 mb-3 text-sm text-gray-700 whitespace-pre-line font-inter">
                        {msg.mensagem}
                      </div>

                      <div className="flex gap-2">
                        <button onClick={() => copyMessage(msg)} data-testid={`copy-msg-child-${idx}`}
                          className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 py-2.5 rounded-xl text-xs font-semibold flex items-center justify-center gap-1.5 transition-all">
                          Copiar Mensagem
                        </button>
                        <button onClick={() => sendWhatsApp(msg, `child-${idx}`)} data-testid={`send-msg-child-${idx}`}
                          className={`flex-1 py-2.5 rounded-xl text-xs font-bold flex items-center justify-center gap-1.5 transition-all ${
                            sentMessages[`child-${idx}`]
                              ? 'bg-green-100 text-green-700'
                              : 'bg-[#25D366] hover:bg-[#20bd5a] text-white'
                          }`}>
                          {sentMessages[`child-${idx}`] ? (
                            <><MessageCircle className="w-3.5 h-3.5" /> Enviado</>
                          ) : (
                            <><Send className="w-3.5 h-3.5" /> Enviar no Grupo</>
                          )}
                        </button>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Volunteers */}
            {voluntarios.length > 0 && (
              <div>
                <h3 className="font-nunito font-bold text-sm text-[#1A4D2E] mb-2 flex items-center gap-2">
                  <UserCheck className="w-4 h-4" /> Voluntarios
                </h3>
                <div className="space-y-3">
                  {voluntarios.map((msg, idx) => (
                    <Card key={idx} className="bg-white rounded-xl p-4 shadow-sm border border-[#E1ECE5]" data-testid={`birthday-vol-${idx}`}>
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <p className="font-nunito font-bold text-base text-[#1A4D2E]">{msg.nome}</p>
                          <p className="font-inter text-xs text-gray-500">{msg.idade} anos - Grupo: <span className="font-semibold text-[#25D366]">{msg.grupo_destino}</span></p>
                        </div>
                        <Cake className="w-6 h-6 text-[#F4D35E]" />
                      </div>

                      <div className="bg-gray-50 rounded-lg p-3 mb-3 text-sm text-gray-700 whitespace-pre-line font-inter">
                        {msg.mensagem}
                      </div>

                      <div className="flex gap-2">
                        <button onClick={() => copyMessage(msg)} data-testid={`copy-msg-vol-${idx}`}
                          className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 py-2.5 rounded-xl text-xs font-semibold flex items-center justify-center gap-1.5 transition-all">
                          Copiar Mensagem
                        </button>
                        <button onClick={() => sendWhatsApp(msg, `vol-${idx}`)} data-testid={`send-msg-vol-${idx}`}
                          className={`flex-1 py-2.5 rounded-xl text-xs font-bold flex items-center justify-center gap-1.5 transition-all ${
                            sentMessages[`vol-${idx}`]
                              ? 'bg-green-100 text-green-700'
                              : 'bg-[#25D366] hover:bg-[#20bd5a] text-white'
                          }`}>
                          {sentMessages[`vol-${idx}`] ? (
                            <><MessageCircle className="w-3.5 h-3.5" /> Enviado</>
                          ) : (
                            <><Send className="w-3.5 h-3.5" /> Enviar no Grupo</>
                          )}
                        </button>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Help */}
            <Card className="bg-amber-50 rounded-xl p-3 border border-amber-200">
              <p className="font-inter text-xs text-amber-700">
                <strong>Como enviar:</strong> Clique "Enviar no Grupo" para abrir o WhatsApp com a mensagem. Cole no grupo correspondente. Para configurar os links dos grupos, va em Administracao &gt; Links WhatsApp.
              </p>
            </Card>
          </>
        )}
      </div>

      <Navigation active="" />
    </div>
  );
};

export default Aniversariantes;
